import { useHead } from '@unhead/react';
import { Button, Flex, Spin } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { useFetchBoardViewerConfig } from 'business/quality-control/hooks/use-fetch-board-viewer-config';
import Routes from 'config/routes';
import { Board } from 'ui/quality-control/board';
import { HourLines } from 'ui/quality-control/board/hour-lines';
import {
  BoardDisplayMode,
  RingData,
  SegmentShapeType,
} from 'ui/quality-control/board/types';
import {
  BoardViewMode,
  getBoardConfiguration,
  getHourLineLabels,
} from 'ui/quality-control/board/utils';

import styles from './index.module.scss';

export function BoardPreviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useHead({
    title: t('user.menu.sections.qualityControl.boardPreview.title'),
  });

  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site selected');

  const {
    segmentDrawConfigs,
    segmentWidth,
    tunnelPositions,
    isLoading,
    error,
    refetch,
  } = useFetchBoardViewerConfig(currentConstructionSiteId);

  const rings = useMemo<RingData[]>(() => {
    if (!segmentDrawConfigs || !tunnelPositions) {
      return [];
    }
    return tunnelPositions.map((position, indexRing) => ({
      id: `0000-00-00-00-0000${position.keyPosition}`,
      type: `P${position.keyPosition}`,
      marginX: position.positionX,
      compliantDate: dayjs().toISOString(),
      hasKeyPosition: true,
      segments: segmentDrawConfigs.map((segment, indexSegment) => ({
        id: `00000${indexRing * 10 + indexSegment}`,
        key: `id-${indexRing}-${segment.order}`,
        label: segment.label,
        shapeType: segment.shape as SegmentShapeType,
        defects: [],
      })),
    }));
  }, [segmentDrawConfigs, tunnelPositions]);

  const boardConfiguration = getBoardConfiguration(
    segmentDrawConfigs,
    segmentWidth,
    rings.length,
    BoardDisplayMode.Preview,
  );

  if (
    error ||
    (!isLoading &&
      (!segmentDrawConfigs.length ||
        !tunnelPositions.length ||
        !boardConfiguration))
  ) {
    return (
      <Flex vertical gap={16} className={styles.page}>
        <Flex gap={8}>
          <Button onClick={() => navigate(Routes.QualityRecordSegmentDefect)}>
            {t('common.go_back')}
          </Button>
          <Button type="primary" onClick={() => refetch()} loading={isLoading}>
            {t('common.refresh')}
          </Button>
          <p>Missing data</p>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex vertical gap={16} className={styles.page}>
      <Flex gap={8}>
        <Button onClick={() => navigate(Routes.QualityRecordSegmentDefect)}>
          {t('common.go_back')}
        </Button>
        <Button type="primary" onClick={() => refetch()} loading={isLoading}>
          {t('common.refresh')}
        </Button>
      </Flex>

      {isLoading || !boardConfiguration ? (
        <Spin />
      ) : (
        <div>
          <svg
            width={boardConfiguration.board.width}
            height={boardConfiguration.board.height}
            className={styles.boardSvg}
          >
            <Board boardConfiguration={boardConfiguration} rings={rings} />

            <HourLines
              labels={getHourLineLabels(BoardViewMode.Entry)}
              totalSegmentWidth={boardConfiguration.ringSegmentsWidth ?? 0}
              marginX={boardConfiguration.margin.leftHourLines ?? 0}
              boardHeight={boardConfiguration.board.height ?? 0}
            />
          </svg>
        </div>
      )}
    </Flex>
  );
}
